import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Package, Percent, Banknote, CalendarDays, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: Package, title: "Free starter pack", desc: "2 × Breethe 30-strip packs, posted to you — try it before you spruik it." },
  { icon: Percent, title: "Your own 10% code", desc: "A personal code for your audience — works on everything, including the $14.95 first pack." },
  { icon: Banknote, title: "15% commission", desc: "Of the product total on every sale your code brings in. Tracked live in your portal." },
  { icon: CalendarDays, title: "30-day trial", desc: "Starts as a 30-day partnership: one genuine Reel/TikTok + 2–3 Stories. Ongoing if it clicks." },
];

const AmbassadorJoin = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null); // null=loading, false=invalid
  const [contract, setContract] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: inv }, { data: c }] = await Promise.all([
          axios.get(`${API}/ambassadors/invite/${token}`),
          axios.get(`${API}/ambassadors/contract`),
        ]);
        setInvite(inv);
        setName(inv.name);
        setContract(c.text);
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (typeof detail === "string") toast.error(detail);
        setInvite(false);
      }
    };
    load();
  }, [token]);

  const onJoin = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Tick the agreement box to continue.");
      return;
    }
    setJoining(true);
    try {
      const { data } = await axios.post(
        `${API}/ambassadors/signup`,
        { token, name: name.trim(), email: invite.email, password, signature: signature.trim() },
        { withCredentials: true }
      );
      toast.success(`You're in! Your code is ${data.code}`);
      navigate("/ambassador", { state: { fresh: true } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't join — try again.");
      setJoining(false);
    }
  };

  if (invite === false) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex flex-col items-center justify-center px-6 text-center">
        <img src="/breethe-logo.webp" alt="Breethe" className="h-8 w-auto mb-8" />
        <h1 className="font-anton uppercase text-3xl md:text-5xl">That link isn't live<span className="text-volt">.</span></h1>
        <p className="mt-4 text-white/60 font-light max-w-md">
          This invite is invalid or has already been used. If you already joined, log in to your portal instead.
        </p>
        <a
          href="/ambassador/login"
          data-testid="ambassador-invalid-login-link"
          className="mt-8 inline-block bg-volt text-black font-anton uppercase tracking-wider text-sm px-8 py-4 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
        >
          Ambassador login
        </a>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex items-center justify-center">
        <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">Loading your invite…</p>
      </div>
    );
  }

  const inputCls =
    "w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-sm text-white outline-none transition-colors duration-300 placeholder:text-white/30";
  const labelCls = "block font-plex text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2";

  return (
    <div data-testid="ambassador-join" className="min-h-screen bg-[#0A0A0A] text-white font-manrope">
      <header className="border-b border-white/10 px-6 md:px-10 h-16 md:h-20 flex items-center">
        <img src="/breethe-logo.webp" alt="Breethe" className="h-6 w-auto" />
      </header>

      <main className="px-6 md:px-10 py-12 md:py-20 max-w-[1100px] mx-auto">
        <p className="font-plex text-xs uppercase tracking-[0.3em] text-volt">Breethe ambassadors</p>
        <h1 className="mt-3 font-anton uppercase text-4xl md:text-7xl leading-[0.9]">
          {invite.name.split(" ")[0]}, you're in<span className="text-volt">.</span>
        </h1>
        <p className="mt-5 text-white/60 font-light text-base md:text-lg max-w-2xl">
          We're building a small crew of ambassadors who genuinely rate Breethe. No fake hype —
          try the product, share it if you love it, and earn on every sale you bring in.
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PERKS.map((p, i) => (
            <div key={p.title} data-testid={`join-perk-${i}`} className="border border-white/10 bg-[#121212] p-6">
              <p.icon size={22} className="text-volt" />
              <p className="mt-4 font-anton uppercase text-lg">{p.title}</p>
              <p className="mt-2 text-white/60 font-light text-sm">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <PenLine size={22} className="text-volt" /> The agreement
          </p>
          <div
            data-testid="join-contract"
            className="mt-5 max-h-72 overflow-y-auto border border-white/10 bg-[#0A0A0A] p-5 text-white/70 font-light text-sm whitespace-pre-wrap"
          >
            {contract}
          </div>

          <form onSubmit={onJoin} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Your name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="join-name-input" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email (locked to your invite)</label>
              <input value={invite.email} disabled data-testid="join-email-input" className={`${inputCls} opacity-50`} />
            </div>
            <div>
              <label className={labelCls}>Choose a password (8+ characters)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="join-password-input"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Sign it — type your full legal name</label>
              <input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                required
                placeholder="YOUR SIGNATURE"
                data-testid="join-signature-input"
                className={inputCls}
              />
            </div>
            <label className="md:col-span-2 flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="join-agree-checkbox"
                className="mt-1 w-4 h-4 accent-[#FF3131]"
              />
              <span className="text-white/60 font-light text-sm">
                I've read the agreement and I'm in — genuine opinions, disclosed posts (#ad), 30-day trial.
              </span>
            </label>
            <button
              type="submit"
              disabled={joining}
              data-testid="join-submit-button"
              className="md:col-span-2 h-14 bg-volt text-black font-anton uppercase tracking-wider text-base transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60"
            >
              {joining ? "Creating your portal…" : "Sign & join the crew"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AmbassadorJoin;
